/**
 * Metadata Collector
 *
 * Collects browser and page metadata for bug reports.
 * Includes page URL, title, user agent, browser info, OS, viewport dimensions, and timestamp.
 *
 * @module utils/metadata
 */

/**
 * Metadata collected from the browser
 */
export interface Metadata {
  /** Current page URL */
  pageUrl: string;

  /** Page title (or empty string if not available) */
  pageTitle: string;

  /** Full user agent string */
  userAgent: string;

  /** Browser name (e.g., "Chrome", "Firefox", "Safari") */
  browserName: string;

  /** Browser version (e.g., "120.0.0") */
  browserVersion: string;

  /** Operating system (e.g., "Windows 10", "macOS 14.0", "Linux") */
  os: string;

  /** Viewport width in pixels */
  viewportWidth: number;

  /** Viewport height in pixels */
  viewportHeight: number;

  /** ISO 8601 timestamp when metadata was collected */
  timestamp: string;
}

/**
 * Browser detection result
 */
interface BrowserInfo {
  name: string;
  version: string;
}

/**
 * Parse User-Agent string to extract browser name and version
 *
 * Uses regex patterns to identify common browsers.
 * Falls back to "Unknown" if no pattern matches.
 *
 * @param userAgent - User agent string
 * @returns Browser name and version
 *
 * @internal
 */
function parseBrowser(userAgent: string): BrowserInfo {
  // Edge (must check before Chrome as it contains "Chrome")
  const edgeMatch = userAgent.match(/Edg(?:e|A|iOS)?\/(\d+(?:\.\d+)*)/);
  if (edgeMatch) {
    return { name: 'Edge', version: edgeMatch[1] };
  }

  // Chrome (must check before Safari as it contains "Safari")
  const chromeMatch = userAgent.match(/Chrome\/(\d+(?:\.\d+)*)/);
  if (chromeMatch && !userAgent.includes('Edg')) {
    return { name: 'Chrome', version: chromeMatch[1] };
  }

  // Firefox
  const firefoxMatch = userAgent.match(/Firefox\/(\d+(?:\.\d+)*)/);
  if (firefoxMatch) {
    return { name: 'Firefox', version: firefoxMatch[1] };
  }

  // Safari (must check after Chrome as Chrome contains "Safari")
  const safariMatch = userAgent.match(/Version\/(\d+(?:\.\d+)*).*Safari/);
  if (safariMatch) {
    return { name: 'Safari', version: safariMatch[1] };
  }

  // Opera
  const operaMatch = userAgent.match(/(?:OPR|Opera)\/(\d+(?:\.\d+)*)/);
  if (operaMatch) {
    return { name: 'Opera', version: operaMatch[1] };
  }

  // Internet Explorer
  const ieMatch = userAgent.match(/MSIE (\d+(?:\.\d+)*)|Trident.*rv:(\d+(?:\.\d+)*)/);
  if (ieMatch) {
    return { name: 'Internet Explorer', version: ieMatch[1] || ieMatch[2] };
  }

  return { name: 'Unknown', version: 'Unknown' };
}

/**
 * Parse User-Agent string to extract operating system
 *
 * Detects Windows, macOS, Linux, iOS, Android, and other platforms.
 *
 * @param userAgent - User agent string
 * @returns Operating system name and version (if available)
 *
 * @internal
 */
function parseOS(userAgent: string): string {
  // Windows
  if (userAgent.includes('Windows NT 10.0')) return 'Windows 10';
  if (userAgent.includes('Windows NT 6.3')) return 'Windows 8.1';
  if (userAgent.includes('Windows NT 6.2')) return 'Windows 8';
  if (userAgent.includes('Windows NT 6.1')) return 'Windows 7';
  if (userAgent.includes('Windows NT')) return 'Windows';

  // macOS
  const macMatch = userAgent.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/);
  if (macMatch) {
    const version = macMatch[1].replace(/_/g, '.');
    return `macOS ${version}`;
  }
  if (userAgent.includes('Macintosh')) return 'macOS';

  // iOS
  const iosMatch = userAgent.match(/(?:iPhone|iPad|iPod).*OS (\d+[._]\d+(?:[._]\d+)?)/);
  if (iosMatch) {
    const version = iosMatch[1].replace(/_/g, '.');
    return `iOS ${version}`;
  }

  // Android
  const androidMatch = userAgent.match(/Android (\d+(?:\.\d+)*)/);
  if (androidMatch) {
    return `Android ${androidMatch[1]}`;
  }

  // Linux
  if (userAgent.includes('Linux')) return 'Linux';

  // ChromeOS
  if (userAgent.includes('CrOS')) return 'ChromeOS';

  return 'Unknown';
}

/**
 * Collect metadata from the current browser environment
 *
 * Gathers page URL, title, user agent, browser information, operating system,
 * viewport dimensions, and timestamp.
 *
 * Handles edge cases:
 * - SSR environments (returns safe defaults)
 * - Missing document.title
 * - Invalid URLs
 *
 * @returns Metadata object with all collected information
 *
 * @example
 * ```typescript
 * const metadata = collectMetadata();
 * console.log(metadata);
 * // {
 * //   pageUrl: "https://example.com/page",
 * //   pageTitle: "Example Page",
 * //   userAgent: "Mozilla/5.0...",
 * //   browserName: "Chrome",
 * //   browserVersion: "120.0.0",
 * //   os: "macOS 14.0",
 * //   viewportWidth: 1920,
 * //   viewportHeight: 1080,
 * //   timestamp: "2025-12-22T10:30:00.000Z"
 * // }
 * ```
 */
export function collectMetadata(): Metadata {
  // Handle SSR or non-browser environments
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      pageUrl: '',
      pageTitle: '',
      userAgent: '',
      browserName: 'Unknown',
      browserVersion: 'Unknown',
      os: 'Unknown',
      viewportWidth: 0,
      viewportHeight: 0,
      timestamp: new Date().toISOString(),
    };
  }

  // Get user agent
  const userAgent = navigator.userAgent || '';

  // Parse browser information
  const browser = parseBrowser(userAgent);

  // Parse OS information
  const os = parseOS(userAgent);

  // Get page URL (with fallback)
  let pageUrl: string;
  try {
    pageUrl = window.location.href;
  } catch {
    pageUrl = '';
  }

  // Get page title (with fallback)
  const pageTitle = document.title || '';

  // Get viewport dimensions
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

  // Get ISO 8601 timestamp
  const timestamp = new Date().toISOString();

  return {
    pageUrl,
    pageTitle,
    userAgent,
    browserName: browser.name,
    browserVersion: browser.version,
    os,
    viewportWidth,
    viewportHeight,
    timestamp,
  };
}
